import { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteTask } from 'api/tasks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface DeleteTaskAlertDialog {
    children: ReactNode;
    taskId: number;
    onSuccess?: () => void;
}
const DeleteTaskAlertDialog = ({ children, taskId, onSuccess }: DeleteTaskAlertDialog) => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    const { mutate: submit } = useMutation<unknown, GivebutterAxiosError>({
        mutationFn: () => deleteTask(accountId, taskId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.tasks(accountId),
            });
            success('Task deleted successfully!');
            onSuccess?.();
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to delete Task. Please try again.');
        },
    });
    return (
        <AlertDialog.Root type="error">
            <AlertDialog.Trigger asChild>{children}</AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete task?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You will not be able to undo this action. Are you sure?
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => submit()}>
                            Delete task
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { DeleteTaskAlertDialog };
